import { getNavbarItems } from '@repo/toolkit/get-navbar-items'
import { useBreakPoint } from '@repo/toolkit/use-break-point'
import { useQuery } from '@tanstack/react-query'
import { createContext, type PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { getAffectedStaffs } from '../../api/affected-staffs/get-affected-staffs'
import { canAccessEnrollmentHealth } from '../../api/enrollments/can-access-enrollment-health'
import { getEnrollmentPickupActions } from '../../api/enrollments/can-modify-enrollment-pickup'
import { getClientReservationActions } from '../../api/enrollments/get-client-reservation-actions'
import { getEnrollment } from '../../api/enrollments/get-enrollment'
import { getEnrollmentCanAccessAdministrationProcedures } from '../../api/enrollments/get-enrollment-adminstration-procedure'
import { getEnrollmentCanAccessDepartureChecklist } from '../../api/enrollments/get-enrollment-departure-checklist'
import { getEnrollmentCanAccessJourneyPreparation } from '../../api/enrollments/get-enrollment-journey-preparation'
import { getContactVersion } from '../../api/enrollments/get-has-transport'
import { enrollmentHasTest } from '../../api/enrollments/has-test'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import { SidebarItemType } from '../../components/elements/sidebar/sidebar-item'
import { isPathnameInSidebarParentUrls } from '../../components/elements/sidebar/utils/sidebar.functions'
import { SidebarItemKey } from '../../components/layouts/main-layout'
import useAuth from '../hooks/use-auth'
import useTranslate from '../hooks/use-translate'
import { AffectedStaffStatusEnum } from '../types/enums/affected-staff/affected-staff-status.enum'
import { ApplicationStatusEnum } from '../types/enums/enrollment/application-status.enum'
import { OLTypeEnum } from '../types/enums/ol/ol-type.enum'
import { RoleEnum } from '../types/enums/users/role.enum'
import { enrollmentManagerSidebarFields } from '../types/fields/enrollments/enrollment'
import { EnrollmentProperties } from '../types/models/enrollment/enrollment.properties'
import { useObserver } from './observer'
import { useSelectedEnrollment } from './selected-enrollment'

type AffectedStaffClickPayload = { id: number }

type SidebarManagerContextType = {
  isSidebarExpanded: boolean
  items: SidebarItemType[]
  search: string

  isItemActive: (itemKey: SidebarItemKey) => boolean
  isItemExpanded: (itemKey: SidebarItemKey) => boolean

  handleSearchChange: (search: string) => void
  handleItemClick: (item: SidebarItemKey, closeSidebar?: boolean) => void
  handleSidebarExpansion: (open: boolean) => void
}

export const SidebarManagerContext = createContext<SidebarManagerContextType>({
  isSidebarExpanded: false,
  items: [],
  search: '',

  isItemActive: () => false,
  isItemExpanded: () => false,

  handleSearchChange: () => {},
  handleItemClick: () => {},
  handleSidebarExpansion: () => {}
})

const SidebarManagerProvider = ({ children }: PropsWithChildren) => {
  // Store last clicked affected staff id to force highlight before route effects settle
  const [selectedAffectedStaffId, setSelectedAffectedStaffId] = useState<number | null>(null)
  const device = useBreakPoint()
  const [expandedItemKey, setExpandedItemKey] = useState<SidebarItemKey>()
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(device !== 'mobile')
  const [search, setSearch] = useState('')
  const { t } = useTranslate()
  const { user } = useAuth()
  const { selectedEnrollmentId, isLoading } = useSelectedEnrollment()
  const staffId = user?.staff?.id
  const location = useLocation()

  const { data: enrollment, isPending: isEnrollmentPending } = useQuery({
    queryKey: ['enrollment', selectedEnrollmentId, enrollmentManagerSidebarFields],
    queryFn: () => {
      if (selectedEnrollmentId)
        return getEnrollment(selectedEnrollmentId, enrollmentManagerSidebarFields)
    },
    enabled: !!selectedEnrollmentId && user?.role === RoleEnum.CLIENT
  })

  const { data: enrollmentAdministrationProcedures } = useQuery({
    queryKey: ['enrollment-administration-procedures-access', enrollment?.id],
    queryFn: () => {
      if (enrollment) return getEnrollmentCanAccessAdministrationProcedures(enrollment.id)
    },
    enabled: !!enrollment
  })

  const { data: enrollmentJourneyPreparation } = useQuery({
    queryKey: ['enrollment-journey-preparation-access', enrollment?.id],
    queryFn: () => {
      if (enrollment) return getEnrollmentCanAccessJourneyPreparation(enrollment.id)
    },
    enabled: !!enrollment
  })

  const { data: enrollmentDepartureChecklist } = useQuery({
    queryKey: ['enrollment-departure-checklist-access', enrollment?.id],
    queryFn: () => {
      if (enrollment) return getEnrollmentCanAccessDepartureChecklist(enrollment.id)
    },
    enabled: !!enrollment
  })

  const { data: getEnrollmentPickupActionsData, isPending: isPickupPending } = useQuery({
    queryKey: ['can-modify-enrollment-pickup', enrollment?.id],
    queryFn: () => enrollment && getEnrollmentPickupActions(enrollment.id),
    enabled: !!enrollment
  })

  const { data: canAccessEnrollmentHealthData, isPending: isHealthPending } = useQuery({
    queryKey: ['can-access-enrollment-health', enrollment?.id],
    queryFn: () => {
      if (enrollment) return canAccessEnrollmentHealth(enrollment.id)
    },
    enabled: !!enrollment
  })

  const { data: enrollmentHasTestData, isPending: isEnrollmentHasTestPending } = useQuery({
    queryKey: ['enrollment-has-test', selectedEnrollmentId],
    queryFn: () => {
      if (selectedEnrollmentId) return enrollmentHasTest(selectedEnrollmentId)
    },
    enabled: !!selectedEnrollmentId && user?.role === RoleEnum.CLIENT
  })
  const { data: clientReservationActions, isPending: isReservationActionsPending } = useQuery({
    queryKey: ['client-reservation-actions', selectedEnrollmentId],
    queryFn: () => {
      if (selectedEnrollmentId) return getClientReservationActions(selectedEnrollmentId)
    },
    enabled: !!selectedEnrollmentId && user?.role === RoleEnum.CLIENT
  })
  const { data: enrollmentContactVersion, isPending: isContactVersionPending } = useQuery({
    queryKey: ['enrollment-contact-version', selectedEnrollmentId],
    queryFn: async () => {
      if (selectedEnrollmentId) return getContactVersion(selectedEnrollmentId)
    },
    enabled: !!selectedEnrollmentId && user?.role === RoleEnum.CLIENT
  })

  const { data: affectedStaffs, isPending: isAffectedStaffsPending } = useQuery({
    queryKey: ['affected-staffs-view', staffId],
    queryFn: () =>
      getAffectedStaffs(
        { filter: { staffId, hasCenters: true, statuses: [AffectedStaffStatusEnum.AFFECTED] } },
        'VIEW'
      ),
    enabled: !!staffId && user?.role === RoleEnum.STAFF
  })

  const enrollmentCancelled =
    enrollment?.status &&
    [
      ApplicationStatusEnum.CANCELLED_AFTER_CONFIRMATION,
      ApplicationStatusEnum.CANCELLED_BEFORE_CONFIRMATION
    ].includes(enrollment.status)

  const filteredItems = useMemo(() => {
    // Helper function to normalize french accents
    const normalizeText = (text?: string) => {
      if (!text) return ''
      return text
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
    }

    const searchRecursively = (item: SidebarItemType, searchTerm: string): boolean => {
      const normalizedText = normalizeText(item.text)
      if (normalizedText.includes(searchTerm)) return true

      if (item.items) return item.items.some((subItem) => searchRecursively(subItem, searchTerm))
      return false
    }

    if (!user || isLoading) return []

    const hideItems: SidebarItemKey[] = []

    if (!enrollmentDepartureChecklist) hideItems.push('client-personal-space-departure-checklist')
    if (!enrollmentAdministrationProcedures)
      hideItems.push('client-personal-space-administration-procedures')
    if (!enrollmentJourneyPreparation) hideItems.push('client-personal-space-journey-preparation')

    // Only hide when we know for sure it is false, not while loading (undefined)
    if (getEnrollmentPickupActionsData?.canView === false)
      hideItems.push('client-personal-space-information-pickup')
    // nothing left to pay once fully paid, no cancellation once the stay has started
    if (clientReservationActions?.canPayBalance === false)
      hideItems.push('client-personal-space-reservations-finalize-payment')
    if (clientReservationActions?.canCancel === false)
      hideItems.push('client-personal-space-reservations-cancel-journey')
    if (enrollment) {
      // a One to One Individuel enrollment has no trip: its participant organises their own
      // travel, so the trips item never applies to it
      if (!enrollment.mainProposition?.mainTrip?.hasTransport)
        hideItems.push('client-personal-space-information-trips')

      if (canAccessEnrollmentHealthData === false)
        hideItems.push('client-personal-space-information-medical-statement')
    }

    // in case of OL Role, we need to hide a couple of items

    // only a prestataire flagged as One to One Individuel reaches the OTO Individuel surface
    if (
      user.role === RoleEnum.Ol &&
      user.ol?.type === OLTypeEnum.PARTNER &&
      !user.ol?.isOtoIndividual
    )
      hideItems.push('ol-oto-individual-places')

    const items = getNavbarItems({
      t,
      role: user.role,
      olType: user.ol?.type,
      options: { hideItems },
      selectedEnrollmentId,
      affectedStaffsData: affectedStaffs?.data,
      enrollmentContactVersion: enrollmentContactVersion?.contactVersion,
      enrollmentHasTest: enrollmentHasTestData,
      enrollmentCancelled,
      cseBillingType: enrollment?.cseRegistration?.cse?.billingType,
      enrollmentIsOtoIndividual: EnrollmentProperties.isOtoIndividual(enrollment)
    })

    return items.filter((item) => {
      if (!search) return true

      const normalizedSearch = normalizeText(search)
      return searchRecursively(item, normalizedSearch)
    })
  }, [
    search,
    user,
    enrollment,
    selectedEnrollmentId,
    isLoading,
    getEnrollmentPickupActionsData,
    clientReservationActions,
    canAccessEnrollmentHealthData,
    affectedStaffs,
    enrollmentContactVersion,
    enrollmentDepartureChecklist,
    enrollmentAdministrationProcedures,
    enrollmentJourneyPreparation,
    enrollmentHasTestData,
    t
  ])

  const getItemParent = (
    item: SidebarItemKey,
    items = filteredItems
  ): SidebarItemType | undefined => {
    for (const currentItem of items) {
      // Check if the item is a direct child
      if (currentItem.items?.some((child) => child.itemKey === item)) {
        return currentItem
      }

      // Recursively check nested items
      if (currentItem.items && currentItem.items.length > 0) {
        const foundParent = getItemParent(item, currentItem.items)
        if (foundParent) return foundParent
      }
    }
    return undefined
  }

  const getItem = (item: SidebarItemKey, items = filteredItems): SidebarItemType | undefined => {
    // Try to find the item directly in the given items
    const searchedItem = items.find((i) => i.itemKey === item)

    // If the item is found directly, return it
    if (searchedItem) return searchedItem

    // If not found, recursively search through the children of each item
    for (const i of items) {
      // If the item has nested items, recursively search those items
      if (i.items && i.items.length > 0) {
        const foundChild = getItem(item, i.items)
        if (foundChild) {
          return foundChild // Return the child if found
        }
      }
    }

    return undefined // Return undefined if the item is not found
  }

  useObserver<AffectedStaffClickPayload>({
    key: 'update-navbar-affected-staffs',
    update: (data) => {
      if (!data || typeof data.id !== 'number') return
      const { id } = data
      setSelectedAffectedStaffId(id)
      const generatedItemKey =
        `staff-personal-space-my-journeys-details-${id}` as unknown as SidebarItemKey
      const item = getItem(generatedItemKey)
      if (item) {
        const parent = getItemParent(generatedItemKey)
        if (parent) setExpandedItemKey(parent.itemKey)
        else setExpandedItemKey(generatedItemKey)
      }
    }
  })

  // Use the get children function to get all items
  const getAllItemsFlattened = (items = filteredItems): SidebarItemType[] => {
    return items.reduce((acc, item) => {
      return acc.concat([item, ...(item.items ? getAllItemsFlattened(item.items) : [])])
    }, [] as SidebarItemType[])
  }

  const getItemChildren = (item: SidebarItemKey) => {
    const itemData = getItem(item)
    if (!itemData) return []
    return (
      itemData.items?.reduce((acc, item) => {
        return acc.concat([item, ...(item.items ?? [])])
      }, [] as SidebarItemType[]) || []
    )
  }

  const activeItemKey = useMemo(() => {
    const activeItems = getAllItemsFlattened().filter((item) =>
      isPathnameInSidebarParentUrls(item, location.pathname)
    )
    if (activeItems.length === 0) return undefined

    // If there's only one matching item, return it
    if (activeItems.length === 1) return activeItems[0].itemKey

    // If multiple items match (e.g., Page C accessible from both Page A and Page B),
    // check location.state.from to determine which parent the user came from
    const referrerPath = (location.state as { from?: string })?.from

    if (referrerPath) {
      // Find the item whose URL or parent's URL matches the referrer
      const itemFromReferrer = activeItems.find((item) => {
        // Check if the item itself matches the referrer
        if (item.url && isPathnameInSidebarParentUrls(item, referrerPath)) return true

        // Check if any parent of this item matches the referrer
        const parent = getItemParent(item.itemKey)
        return parent && isPathnameInSidebarParentUrls(parent, referrerPath)
      })

      if (itemFromReferrer) return itemFromReferrer.itemKey
    }

    // Fallback: return the most specific (deepest) item
    // Items with more parent levels are more specific
    const getDepth = (itemKey: SidebarItemKey, depth = 0): number => {
      const parent = getItemParent(itemKey)
      return parent ? getDepth(parent.itemKey, depth + 1) : depth
    }

    const itemsByDepth = activeItems.sort((a, b) => getDepth(b.itemKey) - getDepth(a.itemKey))
    return itemsByDepth[0]?.itemKey
  }, [filteredItems, location.pathname, location.state])

  /**
   * The item is active if:
   * - The item is the active item
   * - The item is a parent of the active item
   * @param itemKey
   */
  const isItemActive = (itemKey: SidebarItemKey): boolean => {
    if (activeItemKey === itemKey) return true
    const children = getItemChildren(itemKey)
    return children.some((child) => isItemActive(child.itemKey))
  }

  /**
   * The item is expanded if:
   * - The item is the expanded item
   * - The item is a parent of the expanded item
   * @param itemKey
   */
  const isItemExpanded = (itemKey: SidebarItemKey): boolean => {
    if (expandedItemKey === itemKey) return true
    const children = getItemChildren(itemKey)
    return children.some((child) => isItemExpanded(child.itemKey))
  }

  const handleItemClick = (itemKey: SidebarItemKey, closeSidebar = false) => {
    if (isItemExpanded(itemKey)) {
      // check if it has a parent and make it expanded else set it to undefined
      const parentItem = getItemParent(itemKey)

      const item = getItem(itemKey)

      if (parentItem) setExpandedItemKey(parentItem.itemKey)
      else {
        // check if the item has children and set it to undefined
        if (item?.items && item.items.length > 0) setExpandedItemKey(undefined)
      }
    } else setExpandedItemKey(itemKey)

    if (closeSidebar) setIsSidebarExpanded(false)
  }

  useEffect(() => {
    if (activeItemKey) setExpandedItemKey(activeItemKey)
  }, [activeItemKey])

  // When we have a staffId and affectedStaffs data, check the current pathname
  // for a staff journey id. If present, find the generated sidebar item and
  // expand/highlight it so the user sees the correct journey selected.
  useEffect(() => {
    if (!staffId || !affectedStaffs || affectedStaffs.data.length === 0) return
    if (filteredItems.length === 0) return // Wait for items to be loaded

    // Prefer last clicked id (from observer) to ensure immediate highlight, else derive from URL
    const match = location.pathname.match(/\/staff-personal-space\/journey-details\/(\d+)/)
    const derivedId = match ? match[1] : undefined
    const targetId = selectedAffectedStaffId ?? (derivedId ? Number(derivedId) : undefined)
    if (!targetId) return

    const generatedItemKey =
      `staff-personal-space-my-journeys-details-${targetId}` as unknown as SidebarItemKey

    const item = getItem(generatedItemKey)

    if (item) {
      const parent = getItemParent(generatedItemKey)
      if (parent) setExpandedItemKey(parent.itemKey)
      else setExpandedItemKey(generatedItemKey)
    }
  }, [staffId, affectedStaffs, location.pathname, filteredItems, selectedAffectedStaffId])

  useEffect(() => {
    // close sidebar on mobile by default
    setIsSidebarExpanded(device === 'desktop')
  }, [device])

  if (!user) return children

  // Show a loading screen until the necessary data is ready
  const mustWaitForClientData = user.role === RoleEnum.CLIENT && !!selectedEnrollmentId
  const clientDataPending =
    mustWaitForClientData &&
    (isLoading ||
      isEnrollmentPending ||
      isPickupPending ||
      isReservationActionsPending ||
      isHealthPending ||
      isContactVersionPending ||
      isEnrollmentHasTestPending)

  const mustWaitForStaffData = user.role === RoleEnum.STAFF && !!staffId
  const staffDataPending = mustWaitForStaffData && isAffectedStaffsPending

  if (clientDataPending || staffDataPending) return <LoadingScreen />

  return (
    <SidebarManagerContext.Provider
      value={{
        search,
        isSidebarExpanded,
        items: filteredItems,
        isItemActive,
        isItemExpanded,
        handleItemClick,
        handleSearchChange: setSearch,
        handleSidebarExpansion: setIsSidebarExpanded
      }}
    >
      {children}
    </SidebarManagerContext.Provider>
  )
}

export default SidebarManagerProvider
